import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { dateIsBetween } from '@/utils/timeUtils';

import { Box } from '@/components/Box';
import { Icon } from '@/components/Icon';
import { ProductCard } from '@/components/ProductCard';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import type { MoneyFragment } from '@/graphql/saleor/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useNativeCall } from '@/hooks/useNativeCall';
import { useRouter } from '@/hooks/useRouter';
import { useService } from '@/hooks/useService';
import { useUser } from '@/hooks/useUser';
import { QR_CODE_KEY } from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getTranslatedName } from '@/lib/i18n';
import { paths } from '@/lib/paths';
import type { ServiceDetails } from '@/lib/service/type';
import type { StorageHelper } from '@/lib/storage';
import { generateDataTestId } from '@/lib/tests';
import type { SetState, WarrantyResponseCode } from '@/lib/types';
import { queryStringEncrypted } from '@/lib/util';
import { AddAWarranty } from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { WarrantyStatusEnum } from '@/views/MyWatchView/types';

import type { ProductDetail } from '../../MyWatchView';
import { DownloadableCOSC } from '../DownloadableCOSC';
import { DownloadableJewelry } from '../DownloadableJewelry';
import { DownloadableLimitededition } from '../DownloadableLimitededition';
import { DownloadableUserManual } from '../DownloadableUserManual';
import { DownloadableYear140 } from '../DownloadableYear140';
import { InfoLabel } from '../InfoLabel';
import { RemoveFromMyAccount } from '../RemoveFromMyAccount';

import * as M from '../../messages';
import * as S from './DesktopPreview.styles';

export interface MyWatch {
  channel: string;
  isProcessing?: boolean;
  onWarrantyExtend?: () => Promise<void>;
  openModel: SetState<boolean>;
  seckey?: string;
  serviceDetails?: ServiceDetails[];
  storageHelper?: StorageHelper;
  warranty?: string;
  warrantyDuration?: string;
  warrantyPrice?: MoneyFragment;
  warrantyStatus?: WarrantyResponseCode;
  watch: ProductDetail;
}

const checkIsPurchaseBeforeCampaignStart = (
  campaignDate: string,
  purchaseDate: string,
) => {
  const cDate = new Date(campaignDate);
  const pDate = new Date(purchaseDate);

  return pDate < cDate;
};

const checkIsPurchaseAfterCampaignEnd = (
  campaignDate: string,
  purchaseDate: string,
) => {
  const cDate = new Date(campaignDate);
  const pDate = new Date(purchaseDate);

  return pDate > cDate;
};

/**
 * Check if a watch is extendable and was purchased during warranty campaign
 */
export const checkIsEligibleForWarrantyCampaign = (
  isExtendable: boolean,
  warrantyExtensionCampaignEnable: boolean,
  warrantyExtensionCampaignStartDate: string,
  warrantyExtensionCampaignEndDate: string,
  purchaseDate: string,
) => {
  if (
    !warrantyExtensionCampaignEnable ||
    !isExtendable ||
    !purchaseDate ||
    !warrantyExtensionCampaignStartDate ||
    !warrantyExtensionCampaignEndDate
  ) {
    return false;
  }
  const wasPurchasedBeforeCampaignStarted = checkIsPurchaseBeforeCampaignStart(
    warrantyExtensionCampaignStartDate,
    dayjs(purchaseDate).format('YYYY-MM-DD'),
  );
  const wasPurchasedAfterCampaignEnded = checkIsPurchaseAfterCampaignEnd(
    warrantyExtensionCampaignEndDate,
    dayjs(purchaseDate).format('YYYY-MM-DD'),
  );
  return !wasPurchasedBeforeCampaignStarted && !wasPurchasedAfterCampaignEnded;
};

/**
 * Check if a watch is extendable and was purchased outside warranty campaign
 */
export const checkIsEligibleForExtendedWarranty = (
  isExtendable: boolean,
  warrantyExtensionCampaignEnable: boolean,
  warrantyExtensionCampaignStartDate: string,
  warrantyExtensionCampaignEndDate: string,
  purchaseDate: string,
) => {
  if (!warrantyExtensionCampaignEnable) return isExtendable;
  let wasPurchasedBeforeCampaignStarted = false;
  let wasPurchasedAfterCampaignEnded = false;

  if (purchaseDate) {
    wasPurchasedBeforeCampaignStarted = warrantyExtensionCampaignStartDate
      ? checkIsPurchaseBeforeCampaignStart(
          warrantyExtensionCampaignStartDate,
          dayjs(purchaseDate).format('YYYY-MM-DD'),
        )
      : true;
    wasPurchasedAfterCampaignEnded = warrantyExtensionCampaignEndDate
      ? checkIsPurchaseAfterCampaignEnd(
          warrantyExtensionCampaignEndDate,
          dayjs(purchaseDate).format('YYYY-MM-DD'),
        )
      : true;
  }
  return (
    (wasPurchasedBeforeCampaignStarted || wasPurchasedAfterCampaignEnded) &&
    isExtendable
  );
};

export const DesktopPreview = ({
  channel,
  isProcessing,
  onWarrantyExtend,
  openModel,
  seckey = '',
  serviceDetails,
  storageHelper,
  warranty = '',
  warrantyDuration,
  warrantyPrice,
  warrantyStatus = WarrantyStatusEnum.ActiveAndUnbound,
  watch,
}: MyWatch) => {
  const {
    snapshot: { user },
  } = useUser();
  const { navToServiceRequest } = useService();
  const {
    has_cosc_cert,
    has_jewelry_cert,
    has_limit_edition_cert,
    has_year_140_cert,
    product,
  } = watch;
  const { formatMessage } = useIntl();
  const name = getTranslatedName('name', product);
  const { push, query, replace } = useRouter();
  const { country } = useRegionProvider();
  const { initiateNativeCall } = useNativeCall(country.code);
  const { sendEvent } = useGoogleAnalytics();
  const callCustomerService = () => {
    sendEvent({
      event: EVENT_NAME.CONTACT,
      contact_type: 'phone',
    });
    initiateNativeCall();
  };
  const SerialNumber = query.model as string;
  const startTime =
    watch.start_date && dayjs(watch.start_date).format('DD/MM/YYYY');
  const [isEffective, setIsEffective] = useState(true);
  const endTime = watch.end_date && dayjs(watch.end_date).format('DD/MM/YYYY');
  const warrantyStartTime = watch.start_date;
  const warrantyEndTime = watch.end_date;
  useEffect(() => {
    if (warrantyEndTime && warrantyStartTime) {
      const type = dateIsBetween(warrantyStartTime, warrantyEndTime);
      setIsEffective(type);
    }
  }, [warrantyStartTime, warrantyEndTime]);

  const registerWMyWatch = (sku: string, sn: string) => {
    const dataFromQrCode = storageHelper?.getLocalStorage(QR_CODE_KEY);
    const submitData = {
      SN: sn,
      SKU: sku,
      SEC: seckey,
      ...dataFromQrCode,
    };
    push(
      paths.myAccount.login.url({
        query: {
          next: paths.ewarranty.asPath({
            query: submitData,
          }),
          warranty: 'warranty',
        },
      }),
    );
  };

  const viewItInMyWatch = (sn: string, sku: string) => {
    replace(paths.myAccount.myWatches.asPath() + `/${sn}/${sku}`);
  };

  const {
    ecsConfig,
    warrantyExtensionCampaignEnable,
    warrantyExtensionCampaignEndDate,
    warrantyExtensionCampaignStartDate,
  } = useConfigProvider();

  return (
    <S.Wrapper data-test-id='DesktopPreviewWrapper'>
      <S.PictureWrapper data-test-id='DesktopPreviewPictureWrapper'>
        <ProductCard.Image
          alt={`${name} ${watch.sku}`}
          className='watch-soldier-image watch-card'
          data-testid='DesktopPreviewPicture'
          productType='watch'
          sizes='50vw'
          src={product?.mediaLarge?.[0]?.url ?? ''}
          withBackground
          withBackgroundPlaceholderBackground={false}
        />
      </S.PictureWrapper>

      <S.ContentWrapper data-test-id='DesktopPreviewContentWrapper'>
        <S.Content data-test-id='DesktopPreviewContent'>
          <S.WarrantyTitle
            colorized={false}
            data-test-id='DesktopPreviewSectionHeader-breitlingWarranty'
            sizeDesktop={20}
            text={formatMessage(M.messages.breitlingWarranty)}
          />

          <SectionHeader
            data-test-id='DesktopPreviewSectionHeader-name'
            text={name}
          />

          <InfoLabel
            data-test-id='DesktopPreviewInfoLabel-referenceNumber'
            label={formatMessage(M.messages.referenceNumber)}
            value={watch.sku}
          />
          <InfoLabel
            data-test-id='DesktopPreviewInfoLabel-serialNumber'
            label={formatMessage(M.messages.serialNumber)}
            value={SerialNumber}
          />

          <S.WarrantyLabel data-test-id='DesktopPreviewWarrantyLabel'>
            {M.Warranty}
          </S.WarrantyLabel>
          <S.WarrantyBadgeAndDatesWrapper data-test-id='DesktopPreviewWarrantyBadgeAndDatesWrapper'>
            <S.ActiveBadge
              data-test-id='DesktopPreviewActiveBadge'
              isValid={isEffective}
            >
              <Typography>{isEffective ? M.Active : M.Inactive}</Typography>
            </S.ActiveBadge>
            <Box>
              {endTime && (
                <S.ValidityText data-test-id='DesktopPreviewValidityText-endTime'>
                  {formatMessage(M.messages.validUntil)} {endTime}
                </S.ValidityText>
              )}
              {startTime && (
                <S.ValidityText data-test-id='DesktopPreviewValidityText-startTime'>
                  {formatMessage(M.messages.activated)} {startTime}
                </S.ValidityText>
              )}
            </Box>
          </S.WarrantyBadgeAndDatesWrapper>
          {checkIsEligibleForExtendedWarranty(
            watch?.is_extendable,
            warrantyExtensionCampaignEnable,
            warrantyExtensionCampaignStartDate,
            warrantyExtensionCampaignEndDate,
            watch.start_date,
          ) && (
            <>
              <S.WarrantyOperation data-test-id='DesktopPreviewWarrantyOperation'>
                {AddAWarranty(warrantyDuration?.toString())}
              </S.WarrantyOperation>
              {warrantyPrice && (
                <ProductCard.Price
                  data-test-id='DesktopPreviewProductCardPrice'
                  price={warrantyPrice}
                />
              )}
              <S.LinkButton
                colorVariant='blue'
                data-test-id='DesktopPreviewLinkButton-ExtendMyWarranty'
                isFluid
                isProcessing={!!isProcessing}
                label={M.ExtendMyWarranty}
                onClick={() => {
                  sendEvent({ event: EVENT_NAME.INTENTION_WARRANTY_EXTENSION });
                  onWarrantyExtend?.();
                }}
                variant='link'
              >
                <Icon name='chevronRight' size='m' variant='regular' />
              </S.LinkButton>
            </>
          )}
          {!!serviceDetails?.length && ecsConfig?.ecsAvailable && (
            <S.LinkButton
              {...generateDataTestId('button', 'myWatchServiceHistory')}
              colorVariant='blue'
              isFluid
              label={M.MyWatchServiceHistory}
              onClick={() =>
                push(
                  paths.service.details.asPath({
                    params: queryStringEncrypted({
                      sn: SerialNumber,
                      email: user?.email ?? '',
                      loginRequired: true,
                    }),
                  }),
                )
              }
              variant='link'
            >
              <Icon name='chevronRight' size='m' variant='regular' />
            </S.LinkButton>
          )}
          <S.Divider data-test-id='DesktopPreviewDivider' />
          {warranty === '' ? (
            ecsConfig?.ecsAvailable && (
              <S.NeedAServiceButton
                data-test-id='DesktopPreviewNeedAServiceButton-MyWatchNeedsAService'
                isFluid
                label={M.MyWatchNeedsAService}
                onClick={() => navToServiceRequest(SerialNumber)}
                variant='filled'
              />
            )
          ) : (
            <>
              {seckey &&
                warrantyStatus !== WarrantyStatusEnum.ActiveAndUnbound && (
                  <Typography
                    color={
                      warrantyStatus === WarrantyStatusEnum.RegisteredBySelf
                        ? 'success'
                        : 'error'
                    }
                    data-test-id='RegisteredWatchTip'
                    marginBottom={1}
                    scale='p3'
                  >
                    {warrantyStatus === WarrantyStatusEnum.RegisteredBySelf
                      ? M.WatchRegisteredByYourAccount
                      : M.RegisteredError({ callCustomerService })}
                  </Typography>
                )}

              <S.NeedAServiceButton
                data-testid='DesktopPreviewNeedAServiceButton-RegisterMyWatch'
                disabled={
                  warrantyStatus === WarrantyStatusEnum.RegisteredByOther
                }
                isFluid
                label={
                  warrantyStatus === WarrantyStatusEnum.RegisteredBySelf
                    ? M.ViewItInMyWatches
                    : M.RegisterMyWatch
                }
                onClick={() =>
                  warrantyStatus === WarrantyStatusEnum.RegisteredBySelf
                    ? viewItInMyWatch(SerialNumber, watch.sku)
                    : registerWMyWatch(watch.sku, SerialNumber)
                }
                variant='filled'
              />
            </>
          )}
          {warranty === '' && (
            <RemoveFromMyAccount
              data-test-id='DesktopPreviewRemoveFromMyAccount'
              onClick={() => openModel(true)}
            />
          )}

          <S.DownloadablesWrapper
            data-test-id='DesktopPreviewDownloadablesWrapper'
            disableGutters
          >
            {has_cosc_cert && (
              <DownloadableCOSC
                channel={channel}
                data-test-id='DesktopPreviewDownloadableCOSC'
                sn={watch.sn}
              />
            )}
            <DownloadableUserManual
              data-test-id='DesktopPreviewDownloadableUserManual'
              downloadUrl={watch.product.manualUrl?.values[0]?.plainText ?? ''}
            />
            {has_jewelry_cert && (
              <DownloadableJewelry
                channel={channel}
                data-test-id='DesktopPreviewDownloadableJewelry'
                sn={watch.sn}
              />
            )}
            {has_limit_edition_cert && (
              <DownloadableLimitededition
                channel={channel}
                data-test-id='DesktopPreviewDownloadableLimitededition'
                sn={watch.sn}
              />
            )}
            {has_year_140_cert && (
              <DownloadableYear140
                channel={channel}
                data-test-id='DesktopPreviewDownloadableYear140'
                sn={watch.sn}
              />
            )}
          </S.DownloadablesWrapper>
        </S.Content>
      </S.ContentWrapper>
    </S.Wrapper>
  );
};

DesktopPreview.displayName = 'DesktopPreview';
